/**
 * 折扣码应用系统
 * 监听 variant:add 事件，自动应用产品关联的折扣码
 * 支持多折扣码累积（逗号分隔）
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'applied_discount_codes';

  /**
   * 从 localStorage 获取已应用的折扣码列表
   * @returns {Array<string>}
   */
  function getAppliedDiscountCodes() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('读取折扣码失败:', error);
      return [];
    }
  }

  /**
   * 保存折扣码列表到 localStorage
   * @param {Array<string>} codes
   */
  function saveAppliedDiscountCodes(codes) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
    } catch (error) {
      console.error('保存折扣码失败:', error);
    }
  }

  /**
   * 添加新折扣码到列表（去重）
   * @param {string} newCode
   * @returns {Array<string>}
   */
  function addDiscountCode(newCode) {
    if (!newCode || newCode.trim() === '') {
      return getAppliedDiscountCodes();
    }

    const codes = getAppliedDiscountCodes();
    const trimmedCode = newCode.trim().toUpperCase();

    // 去重：如果折扣码已存在，不重复添加
    if (!codes.includes(trimmedCode)) {
      codes.push(trimmedCode);
      saveAppliedDiscountCodes(codes);
      // console.log('✅ 折扣码已添加:', trimmedCode);
    } else {
      // console.log('ℹ️ 折扣码已存在:', trimmedCode);
    }

    return codes;
  }

  /**
   * 获取合并后的折扣码字符串（逗号分隔）
   * @returns {string}
   */
  function getMergedDiscountCodesString() {
    const codes = getAppliedDiscountCodes();
    return codes.join(',');
  }

  /**
   * 应用折扣码到购物车
   * @param {string} discountCode - 新的折扣码
   */
  async function applyDiscountToCart(discountCode) {
    if (!discountCode || discountCode.trim() === '') {
      // console.log('⚠️ 没有折扣码需要应用');
      return;
    }

    // 添加新折扣码到列表
    addDiscountCode(discountCode);

    // 获取合并后的所有折扣码
    const mergedCodes = getMergedDiscountCodesString();

    if (!mergedCodes) {
      // console.log('⚠️ 没有折扣码需要应用');
      return;
    }

    // console.log('📌 准备应用折扣码:', mergedCodes);

    try {
      // 收集需要刷新的 sections
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      // 创建 FormData
      const formData = new FormData();
      formData.append('discount', mergedCodes);
      
      // 添加 sections 参数（用于局部刷新 UI）
      if (sectionsToBundle.length > 0) {
        formData.append('sections', sectionsToBundle.join(','));
      }

      // 发送 update 请求
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const cartData = await response.json();
        // console.log('✅ 折扣码已应用到购物车');
        // console.log('购物车数据:', cartData);
        // console.log('总折扣:', cartData.total_discount);
        // console.log('应用的折扣:', cartData.cart_level_discount_applications);

        // 触发购物车更新事件
        // 注意：cartData 已经包含 sections 数据（如果请求了的话）
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'discount:apply',
              cart: cartData
            }
          })
        );
      } else {
        console.error('❌ 折扣码应用失败:', response.status);
      }
    } catch (error) {
      console.error('❌ 折扣码应用异常:', error);
    }
  }

  /**
   * 从表单中提取折扣码
   * @param {HTMLFormElement} form
   * @returns {string|null}
   */
  function extractDiscountCodeFromForm(form) {
    // 方式 1：从 hidden input 读取
    const discountInput = form.querySelector('input[name="discount"]');
    if (discountInput && discountInput.value) {
      return discountInput.value;
    }

    // 方式 2：从 form 的 data 属性读取
    if (form.dataset.discountCode) {
      return form.dataset.discountCode;
    }

    // 方式 3：从关联的 buy-buttons 元素读取
    if (form.id) {
      const buyButtons = document.querySelector(`buy-buttons[form="${form.id}"]`);
      if (buyButtons && buyButtons.dataset.discountCode) {
        return buyButtons.dataset.discountCode;
      }
    }

    return null;
  }

  /**
   * 监听 variant:add 事件
   */
  function initializeDiscountApply() {
    // console.log('🔗 折扣码应用系统已加载');

    // 监听表单级别的 variant:add 事件
    document.addEventListener('variant:add', async (event) => {
      // console.log('📦 检测到商品添加事件');

      // 尝试从事件源（表单）获取折扣码
      const form = event.target;
      if (form && form.tagName === 'FORM') {
        const discountCode = extractDiscountCodeFromForm(form);

        if (discountCode) {
          // console.log('🎫 发现折扣码:', discountCode);

          // 延迟执行，确保 add 请求完成
          setTimeout(async () => {
            await applyDiscountToCart(discountCode);
          }, 300);
        } else {
          // console.log('ℹ️ 此商品没有关联折扣码');
        }
      }
    });

    // 监听全局的 cart:change 事件（可选）
    document.documentElement.addEventListener('cart:change', (event) => {
      if (event.detail.baseEvent === 'variant:add') {
        // console.log('🛒 购物车已更新');
      }
    });
  }

  /**
   * 清空所有已应用的折扣码（调试用）
   */
  window.clearAppliedDiscountCodes = function() {
    localStorage.removeItem(STORAGE_KEY);
    // console.log('🗑️ 已清空所有折扣码记录');

    // 同时清空购物车的折扣码
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discount: '' })
    }).then(() => {
      // console.log('✅ 购物车折扣码已清空');
    });
  };

  /**
   * 查看当前已应用的折扣码（调试用）
   */
  window.getAppliedDiscountCodes = function() {
    const codes = getAppliedDiscountCodes();
    // console.log('📋 当前已应用的折扣码:', codes);
    // console.log('合并字符串:', getMergedDiscountCodesString());
    return codes;
  };

  // 页面加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDiscountApply);
  } else {
    initializeDiscountApply();
  }

  // 页面显示时（从缓存恢复）重新检查
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      // console.log('🔄 页面从缓存恢复');
    }
  });

})();
